import { useI18n } from "@core/i18n/i18n";
import { libraryFilterManager } from "@core/service-container";
import styled from "@emotion/styled";
import { OnboardingPanel } from "@ui/components/onboarding/onboarding-panel";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { useExerciseLibrary } from "@ui/pages/program-builder/hooks/use-exercise-library";
import { SearchFilterComponent } from "@ui/pages/program-builder/library/components/search-filter-component";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row } from "@ui/style";
import { ExerciseLibraryCounts } from "@ui/pages/program-builder/library/sidebar/exercise-library-counts";
import { useObservable } from "micro-observables";
import React, { useCallback, useMemo } from "react";
import { FilterItem, FilterItemStatus } from "@ui/components/filters/filter-item";
import { ExerciseFilter } from "@domain/library/library-filter-manager";
import { ClickableText } from "@ui/components/common/clickable-text";
import { useLibraryGuides } from "@ui/pages/program-builder/library/hooks/use-library-guides";
import { hideComplementaryGuide3 } from "@ui/pages/program-builder/quickstart/complementary-tooltip-signal";
import { hideAlternativeGuide3 } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import { EmptySearch } from "@ui/pages/exercise/components/empty-search";

export const ExerciseLibrarySearchFilter: React.FC<{
	showFilterLibraryGuidance?: boolean;
	onHideFilterLibraryGuidance?: () => void;
}> = ({ showFilterLibraryGuidance, onHideFilterLibraryGuidance }) => {
	const { format } = useI18n();
	const { isDesktop } = useDimensions();
	const { isAnyFilterActive, filteredSelectedExercises: filteredPreSelectedExercises, filteredExercises } = useExerciseLibrary();
	const activeCategoryFilters = useObservable(libraryFilterManager.activeCategoryFilters);

	const onFilterClear = useCallback(
		(filter: ExerciseFilter) => {
			libraryFilterManager.activeCategoryFilters.set(
				activeCategoryFilters.map(cat => ({
					...cat,
					filters: cat.filters.filter(f => f.id !== filter.id),
				})),
			);
			hideComplementaryGuide3();
			hideAlternativeGuide3();
		},
		[activeCategoryFilters],
	);

	const onClearAll = useCallback(() => {
		libraryFilterManager.activeCategoryFilters.set([]);
		hideComplementaryGuide3();
		hideAlternativeGuide3();
		libraryFilterManager.clearActiveFilteringState();
	}, []);
	const { showComplementaryGuide3Panel, showAlternativeGuide3Panel, onCloseOnboardingPanel } = useLibraryGuides();

	const activeFiltersCount = useMemo(
		() =>
			activeCategoryFilters.reduce((acc, category) => acc + category.filters.reduce(accByCat => accByCat + 1, 0), 0),
		[activeCategoryFilters],
	);

	const areFilteredExercisesPresent = useMemo(
		() => isAnyFilterActive && (!isDesktop || filteredPreSelectedExercises.length > 0 || filteredExercises.length > 0),
		[filteredExercises.length, filteredPreSelectedExercises.length, isAnyFilterActive, isDesktop],
	);


	return (
		<>
			<SearchFilterContainer>
				<SearchFilterComponent
					activeFiltersCount={activeFiltersCount}
					showFilterLibraryGuidance={showFilterLibraryGuidance}
					onHideFilterLibraryGuidance={onHideFilterLibraryGuidance}
				/>
				{/* TODO: following code is a duplication of ActiveFilterListing, it should be removed and use 
			<ActiveFilterListing /> */}
				{activeCategoryFilters.length > 0 && (
					<TagContainer>
						{activeCategoryFilters.map(category =>
							category.filters.map(filter => (
								<FilterItem
									key={filter.id}
									filter={filter}
									status={FilterItemStatus.Tag}
									onChange={() => {
										onFilterClear(filter);
									}}
								/>
							)),
						)}
						<ClickableText onClick={onClearAll}>{format("exerciseFilterListingPanel.clear")}</ClickableText>
					</TagContainer>
				)}

				{(showComplementaryGuide3Panel || showAlternativeGuide3Panel) && (
					<StyledOnboardingPanel
						text={format(
							showAlternativeGuide3Panel
								? "programBuilder.quickstart.step.addAlternativeExercises.guide03"
								: "programBuilder.quickstart.step.complementaryExercises.guide03",
						)}
						onClose={onCloseOnboardingPanel}
					/>
				)}
			</SearchFilterContainer>
			{areFilteredExercisesPresent && <ExerciseLibraryCounts mode="SearchResult" />}
			{areFilteredExercisesPresent && filteredPreSelectedExercises.length === 0 && filteredExercises.length === 0 && (
				<EmptySearch />
			)}
		</>
	);
};

const SearchFilterContainer = styled.div`
	margin: 0px 20px 0px 20px;
	@media ${physioMeMobileBreakpoint} {
		margin: 0px 20px 0px 0px;
	}
`;

const TagContainer = styled.div`
	margin: 12px 0px 0;
	${row("XS", "flexstart", "center")};
	flex-wrap: wrap;
`;

const StyledOnboardingPanel = styled(OnboardingPanel)`
	margin: 10px 0px;
	@media ${physioMeTabletBreakpoint} {
		margin-left: 0px;
		margin-right: 16px;
	}
`;
